import type { OrderStatus } from "@/redux/services/order/order.type";

/** Order statuses shown on the kitchen board. */
export const KDS_STATUSES: readonly OrderStatus[] = [
  "accepted",
  "preparing",
  "ready",
];

export interface KdsChimeOrder {
  id: string;
  status: OrderStatus;
  hasUnpreparedItems?: boolean;
}

/**
 * Decides when the kitchen board rings for an order event.
 *
 * - A new order rings the first time it shows up in a kitchen status,
 *   whatever the event. `order:created` is published before POS auto-accept,
 *   so it carries `pending`; the ring then comes with the `order:updated`
 *   that moves it to `accepted`. Orders seen outside the kitchen (pending)
 *   are not remembered, so that later update still rings.
 * - A served order rings once when an add-on brings unprepared work, and
 *   again for a later add-on after that work is done.
 * - Orders already on the board at load (`seed`, `seedAddOns`) never ring.
 */
export const createKdsChime = () => {
  const seenKdsIds = new Set<string>();
  let seenAddOnIds = new Set<string>();

  return {
    /** Orders on the board at first load. Adds to what is already known. */
    seed(ids: Iterable<string>) {
      for (const id of ids) seenKdsIds.add(id);
    },

    /** Served orders currently showing as add-on work (replaces the list). */
    seedAddOns(ids: Iterable<string>) {
      seenAddOnIds = new Set(ids);
    },

    shouldRing(order: KdsChimeOrder): boolean {
      if (KDS_STATUSES.includes(order.status)) {
        if (seenKdsIds.has(order.id)) return false;
        seenKdsIds.add(order.id);
        return true;
      }

      if (order.status === "served") {
        if (!order.hasUnpreparedItems) {
          seenAddOnIds.delete(order.id);
          return false;
        }
        if (seenAddOnIds.has(order.id)) return false;
        seenAddOnIds.add(order.id);
        return true;
      }

      return false;
    },
  };
};
